import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Edit, Trash2, DollarSign, Loader2, Search, 
  Table2, Armchair, Shirt, UtensilsCrossed, Sparkles, Users, Package
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Rentals = () => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    category: 'tables',
    sub_category: '',
    description: '',
    price_per_unit: '',
    unit: 'each',
    color: '',
    dimensions: ''
  });

  const fetchRentals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/rentals`);
      setRentals(response.data);
    } catch (error) {
      toast({ title: 'Error loading rentals', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchRentals(); }, [fetchRentals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price_per_unit: parseFloat(formData.price_per_unit),
        price: parseFloat(formData.price_per_unit) // Keep both for compatibility
      };

      if (editingItem) {
        await axios.put(`${API}/rentals/${editingItem.id}`, data);
        toast({ title: 'Rental item updated!' });
      } else {
        await axios.post(`${API}/rentals`, data);
        toast({ title: 'Rental item added!' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchRentals();
    } catch (error) {
      toast({ title: 'Error saving rental', description: error.message, variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category: 'tables', sub_category: '', description: '', price_per_unit: '', unit: 'each', color: '', dimensions: '' });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      category: item.category || 'tables',
      sub_category: item.sub_category || '',
      description: item.description || '',
      price_per_unit: (item.price_per_unit || item.price || 0).toString(),
      unit: item.unit || 'each',
      color: item.color || '',
      dimensions: item.dimensions || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rental item?')) return;
    try {
      await axios.delete(`${API}/rentals/${id}`);
      toast({ title: 'Rental item deleted!' });
      fetchRentals();
    } catch (error) {
      toast({ title: 'Error deleting rental', description: error.message, variant: 'destructive' });
    }
  };

  // Category configuration with icons and colors
  const categoryConfig = {
    tables: { label: 'Tables', icon: Table2, color: 'from-blue-600 to-blue-400' },
    chairs: { label: 'Chairs', icon: Armchair, color: 'from-purple-600 to-purple-400' },
    linens: { label: 'Linens', icon: Shirt, color: 'from-pink-600 to-pink-400' },
    china: { label: 'China', icon: UtensilsCrossed, color: 'from-amber-600 to-amber-400' },
    flatware: { label: 'Flatware', icon: UtensilsCrossed, color: 'from-gray-600 to-gray-400' },
    glassware: { label: 'Glassware', icon: UtensilsCrossed, color: 'from-cyan-600 to-cyan-400' },
    serving: { label: 'Serving', icon: Package, color: 'from-green-600 to-green-400' },
    decor: { label: 'Decor', icon: Sparkles, color: 'from-rose-600 to-rose-400' },
    services: { label: 'Services', icon: Users, color: 'from-indigo-600 to-indigo-400' },
    venue: { label: 'Venue', icon: Package, color: 'from-emerald-600 to-emerald-400' },
    centerpieces: { label: 'Centerpieces', icon: Sparkles, color: 'from-violet-600 to-violet-400' },
    chair_covers: { label: 'Chair Covers', icon: Armchair, color: 'from-fuchsia-600 to-fuchsia-400' },
    bar_addon: { label: 'Bar Add-ons', icon: UtensilsCrossed, color: 'from-orange-600 to-orange-400' },
    bar_service: { label: 'Bar Service', icon: UtensilsCrossed, color: 'from-red-600 to-red-400' }
  };

  // Get unique categories from data
  const availableCategories = useMemo(() => {
    const cats = [...new Set(rentals.map(r => r.category))].filter(Boolean);
    return cats.sort();
  }, [rentals]);

  // Filter rentals
  const filteredRentals = useMemo(() => {
    let filtered = rentals;
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(r => r.category === categoryFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.name?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query) ||
        r.sub_category?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [rentals, categoryFilter, searchQuery]);

  // Group by sub_category for display
  const groupedRentals = useMemo(() => {
    const groups = {};
    filteredRentals.forEach(item => {
      const key = item.sub_category || 'general';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [filteredRentals]);

  const getPrice = (item) => item.price_per_unit || item.price || item.item_price || 0;

  const getCategoryIcon = (category) => {
    const Icon = categoryConfig[category]?.icon || Package;
    return <Icon size={16} />;
  };

  const getCategoryColor = (category) => {
    return categoryConfig[category]?.color || 'from-gray-600 to-gray-400';
  };

  // Stats
  const stats = useMemo(() => {
    return {
      total: rentals.length,
      tables: rentals.filter(r => r.category === 'tables').length,
      chairs: rentals.filter(r => r.category === 'chairs').length,
      linens: rentals.filter(r => r.category === 'linens').length,
      china: rentals.filter(r => ['china', 'flatware', 'glassware'].includes(r.category)).length,
      serving: rentals.filter(r => r.category === 'serving').length,
      decor: rentals.filter(r => r.category === 'decor').length,
      services: rentals.filter(r => r.category === 'services').length
    };
  }, [rentals]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="rentals-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Rentals & Equipment</h1>
          <p className="text-gray-600">Tables, chairs, linens, china, decor, and services</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="add-rental-btn">
              <Plus size={20} className="mr-2" />Add Item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Rental Item' : 'Add New Rental Item'}</DialogTitle>
              <DialogDescription>Fill in the details for this rental item</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Item Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tables">Tables</SelectItem>
                      <SelectItem value="chairs">Chairs</SelectItem>
                      <SelectItem value="linens">Linens</SelectItem>
                      <SelectItem value="china">China</SelectItem>
                      <SelectItem value="flatware">Flatware</SelectItem>
                      <SelectItem value="glassware">Glassware</SelectItem>
                      <SelectItem value="serving">Serving Equipment</SelectItem>
                      <SelectItem value="decor">Decor</SelectItem>
                      <SelectItem value="centerpieces">Centerpieces</SelectItem>
                      <SelectItem value="services">Services</SelectItem>
                      <SelectItem value="venue">Venue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sub-Category</Label>
                  <Input placeholder="e.g., banquet, chiavari, napkins" value={formData.sub_category} onChange={(e) => setFormData({ ...formData, sub_category: e.target.value })} />
                </div>
                <div>
                  <Label>Color</Label>
                  <Input placeholder="e.g., White, Gold, Black" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Price ($) *</Label>
                  <Input type="number" step="0.01" value={formData.price_per_unit} onChange={(e) => setFormData({ ...formData, price_per_unit: e.target.value })} required />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="each">Each</SelectItem>
                      <SelectItem value="set">Set</SelectItem>
                      <SelectItem value="pair">Pair</SelectItem>
                      <SelectItem value="per person">Per Person</SelectItem>
                      <SelectItem value="per event">Per Event</SelectItem>
                      <SelectItem value="per hour">Per Hour</SelectItem>
                      <SelectItem value="per station">Per Station</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Dimensions</Label>
                  <Input placeholder='e.g., 72" x 30"' value={formData.dimensions} onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })} />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold">
                {editingItem ? 'Update Item' : 'Add Item'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'bg-gray-100' },
          { label: 'Tables', value: stats.tables, color: 'bg-blue-100' },
          { label: 'Chairs', value: stats.chairs, color: 'bg-purple-100' },
          { label: 'Linens', value: stats.linens, color: 'bg-pink-100' },
          { label: 'Tableware', value: stats.china, color: 'bg-amber-100' },
          { label: 'Serving', value: stats.serving, color: 'bg-green-100' },
          { label: 'Decor', value: stats.decor, color: 'bg-rose-100' },
          { label: 'Services', value: stats.services, color: 'bg-indigo-100' }
        ].map((stat, idx) => (
          <div key={idx} className={`${stat.color} p-3 rounded-lg text-center`}>
            <p className="text-2xl font-bold text-[#1e3a5f]">{stat.value}</p>
            <p className="text-xs text-gray-600">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input 
            placeholder="Search items..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white"
            data-testid="search-rentals"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="mb-6">
        <TabsList className="bg-white flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
            All ({rentals.length})
          </TabsTrigger>
          {availableCategories.map(cat => (
            <TabsTrigger 
              key={cat} 
              value={cat}
              className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white capitalize"
            >
              {getCategoryIcon(cat)}
              <span className="ml-1">{categoryConfig[cat]?.label || cat}</span>
              <span className="ml-1 opacity-70">({rentals.filter(r => r.category === cat).length})</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1e3a5f]" size={48} /></div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedRentals).map(([subCat, items]) => (
            <div key={subCat}>
              {categoryFilter !== 'all' && (
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-3 capitalize border-b pb-2">
                  {subCat.replace(/_/g, ' ')} ({items.length})
                </h3>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map((item) => (
                  <Card key={item.id} className="border-none shadow-md hover:shadow-lg transition-all" data-testid={`rental-${item.id}`}>
                    <CardHeader className={`bg-gradient-to-r ${getCategoryColor(item.category)} text-white rounded-t-lg py-3`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-sm font-semibold leading-tight">{item.name}</CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs opacity-80 capitalize">{item.category?.replace(/_/g, ' ')}</span>
                            {item.sub_category && (
                              <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded capitalize">{item.sub_category.replace(/_/g, ' ')}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">${getPrice(item).toFixed(2)}</p>
                          <p className="text-xs opacity-80">{item.unit || 'each'}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-3">
                      {item.description && (
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">{item.description}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {item.color && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{item.color}</span>
                        )}
                        {item.dimensions && (
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{item.dimensions}</span>
                        )}
                        {item.seats && (
                          <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">Seats {item.seats}</span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button onClick={() => handleEdit(item)} variant="outline" size="sm" className="flex-1 h-7 text-xs">
                          <Edit size={12} className="mr-1" />Edit
                        </Button>
                        <Button onClick={() => handleDelete(item.id)} variant="outline" size="sm" className="h-7 text-xs text-red-500 border-red-200 hover:bg-red-50">
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
          
          {filteredRentals.length === 0 && (
            <div className="text-center py-20">
              <Package className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500">No items found</p>
              <p className="text-sm text-gray-400 mt-2">Try adjusting your search or filter</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Rentals;
